import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  CurrencyRupeeIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline'
import { supabase } from '../../config/supabase'
import cashfreeService from '../../services/cashfreeService'

const PlatformRevenueAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalRestaurants: 0,
    activeRestaurants: 0,
    totalOrders: 0,
    monthlyOrders: 0,
    averageOrderValue: 0,
    commissionRate: 3.5,
    projectedAnnualRevenue: 0,
    restaurantSavings: 0
  })
  const [loading, setLoading] = useState(true)
  const [timeframe, setTimeframe] = useState('month') // 'week', 'month', 'year'

  useEffect(() => {
    fetchPlatformAnalytics()
  }, [timeframe])

  const fetchPlatformAnalytics = async () => {
    try {
      setLoading(true)
      
      // Calculate date ranges
      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const startOfYear = new Date(now.getFullYear(), 0, 1)
      
      // Fetch total restaurants
      const { data: restaurants, error: restaurantsError } = await supabase
        .from('users')
        .select('id, restaurant_name, created_at')
        .eq('role', 'restaurant_owner')
        .not('restaurant_name', 'is', null)

      if (restaurantsError) throw restaurantsError

      // Fetch orders data
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          total_amount,
          created_at,
          status,
          payment_status,
          restaurant_id,
          tip_amount
        `)
        .eq('status', 'completed')
        .eq('payment_status', 'completed')

      if (ordersError) throw ordersError

      // Calculate analytics
      const totalRestaurants = restaurants?.length || 0
      const activeRestaurants = restaurants?.filter(r => {
        // Restaurant is active if they have orders in last 30 days
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        return orders?.some(o => o.restaurant_id === r.id && new Date(o.created_at) > thirtyDaysAgo)
      }).length || 0

      const totalOrders = orders?.length || 0
      const monthlyOrders = orders?.filter(o => new Date(o.created_at) >= startOfMonth).length || 0

      // Calculate revenue (3.5% commission)
      const totalOrderValue = orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0
      const monthlyOrderValue = orders
        ?.filter(o => new Date(o.created_at) >= startOfMonth)
        ?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0

      const totalRevenue = totalOrderValue * 0.035 // 3.5% commission
      const monthlyRevenue = monthlyOrderValue * 0.035

      const averageOrderValue = totalOrders > 0 ? totalOrderValue / totalOrders : 0
      const projectedAnnualRevenue = monthlyRevenue * 12

      // Calculate restaurant savings vs delivery platforms (30% commission)
      const restaurantSavings = totalOrderValue * (0.30 - 0.035) // 26.5% savings

      setAnalytics({
        totalRevenue,
        monthlyRevenue,
        totalRestaurants,
        activeRestaurants,
        totalOrders,
        monthlyOrders,
        averageOrderValue,
        commissionRate: 3.5,
        projectedAnnualRevenue,
        restaurantSavings,
        totalOrderValue,
        monthlyOrderValue
      })

    } catch (error) {
      console.error('❌ Error fetching platform analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (amount >= 10000000) { // 1 crore
      return `₹${(amount / 10000000).toFixed(2)}Cr`
    } else if (amount >= 100000) { // 1 lakh
      return `₹${(amount / 100000).toFixed(2)}L`
    } else if (amount >= 1000) { // 1 thousand
      return `₹${(amount / 1000).toFixed(1)}K`
    }
    return `₹${amount.toFixed(0)}`
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'orange', trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${
            trend > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            <ArrowTrendingUpIcon className="w-3 h-3" />
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
      </div>
    </motion.div>
  )

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Platform Revenue Analytics
        </h1>
        <p className="text-gray-600">
          QR Restaurant SaaS - 3.5% Commission Model Performance
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Platform Revenue"
          value={formatCurrency(analytics.totalRevenue)}
          subtitle="3.5% commission earned"
          icon={CurrencyRupeeIcon}
          color="green"
        />
        
        <StatCard
          title="Monthly Revenue"
          value={formatCurrency(analytics.monthlyRevenue)}
          subtitle={`${analytics.monthlyOrders} orders this month`}
          icon={ChartBarIcon}
          color="blue"
        />
        
        <StatCard
          title="Active Restaurants"
          value={`${analytics.activeRestaurants}/${analytics.totalRestaurants}`}
          subtitle="Restaurants with recent orders"
          icon={BuildingStorefrontIcon}
          color="purple"
        />
        
        <StatCard
          title="Total Orders"
          value={analytics.totalOrders.toLocaleString()}
          subtitle={`₹${analytics.averageOrderValue.toFixed(0)} avg order value`}
          icon={UsersIcon}
          color="orange"
        />
      </div>

      {/* Business Model Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Breakdown */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Menu Commission (2%)</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(analytics.totalOrderValue * 0.02)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Platform Fee (1.5%)</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(analytics.totalOrderValue * 0.015)}
              </span>
            </div>
            <div className="border-t pt-2">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900">Total Commission (3.5%)</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(analytics.totalRevenue)}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Restaurant Savings */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Restaurant Savings</h3>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(analytics.restaurantSavings)}
              </div>
              <div className="text-sm text-gray-600">
                Total saved vs delivery platforms
              </div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Delivery Platforms (30%)</span>
                <span className="text-red-600 font-medium">
                  {formatCurrency(analytics.totalOrderValue * 0.30)}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Our Platform (3.5%)</span>
                <span className="text-green-600 font-medium">
                  {formatCurrency(analytics.totalRevenue)}
                </span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between text-sm font-bold">
                  <span className="text-gray-900">Savings (87% cheaper)</span>
                  <span className="text-green-600">
                    {formatCurrency(analytics.restaurantSavings)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Growth Projections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Projection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Monthly Growth</h3>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {formatCurrency(analytics.monthlyRevenue)}
            </div>
            <div className="text-sm text-gray-600 mb-4">Current monthly revenue</div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xs text-blue-700 font-medium">
                Target: ₹5.25L (25 restaurants)
              </div>
              <div className="text-xs text-blue-600">
                Progress: {((analytics.monthlyRevenue / 525000) * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </motion.div>

        {/* Annual Projection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Annual Projection</h3>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-1">
              {formatCurrency(analytics.projectedAnnualRevenue)}
            </div>
            <div className="text-sm text-gray-600 mb-4">Projected annual revenue</div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-xs text-purple-700 font-medium">
                Target: ₹37.8Cr (150 restaurants)
              </div>
              <div className="text-xs text-purple-600">
                Scale Phase Goal
              </div>
            </div>
          </div>
        </motion.div>

        {/* Unit Economics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Unit Economics</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Revenue per Order</span>
              <span className="font-medium text-gray-900">
                ₹{(analytics.averageOrderValue * 0.035).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Revenue per Restaurant</span>
              <span className="font-medium text-gray-900">
                {analytics.activeRestaurants > 0 
                  ? formatCurrency(analytics.monthlyRevenue / analytics.activeRestaurants)
                  : '₹0'
                }
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Orders per Restaurant</span>
              <span className="font-medium text-gray-900">
                {analytics.activeRestaurants > 0 
                  ? Math.round(analytics.monthlyOrders / analytics.activeRestaurants)
                  : 0
                }/month
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Business Model Validation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200"
      >
        <h3 className="text-lg font-bold text-gray-900 mb-4">Business Model Validation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">87%</div>
            <div className="text-sm text-gray-700">Cheaper than delivery</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">3.5%</div>
            <div className="text-sm text-gray-700">Sustainable commission</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {analytics.activeRestaurants}
            </div>
            <div className="text-sm text-gray-700">Network restaurants</div>
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            <strong>Success Metrics:</strong> Self-funding by month 6 • 
            Network effects at 25+ restaurants • 
            IPO-ready at ₹250Cr+ annual revenue
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default PlatformRevenueAnalytics
